/**
 * A score badge that explains itself: hover or focus it for the breakdown — each
 * scorecard line, the person's figure against their peers' average, and the points
 * it earned — plus the penalty and who the peers are.
 *
 * Asked for on 2026-10-02 (Yehuda): a bare "+6.5" said nothing about how it was
 * formed. The card is the "i" card's (components/Info.tsx), so it opens, closes and
 * stays on screen the same way, and it escapes the table cards that clip overflow.
 *
 * The trigger lets clicks through: in a clickable row, clicking the score still opens
 * the person.
 */
import type { ReactNode } from 'react'
import { useHoverCard } from './Info'
import type { Scorecard, ScoreKey } from '@/measures/scorecard'
import { formatPoints, SCORE_MAX, SCORE_MIN, PENALTY, unscoredReason, lowVolumeReason } from '@/measures/scorecard'
import { MINIMUM_VOLUME_LABEL } from '@/measures/minimumVolume'
import { money2, int, pct, dec } from '@/format'

const CARD_WIDTH = 340

/** Each line's figure, as the rest of the app prints it. */
const FORMAT: Record<ScoreKey, (v: number | null) => string> = {
  costPerClaim: money2,
  claims: (v) => (v === null ? '—' : int(Math.round(v))),
  collectedPct: (v) => pct(v),
  closedClaimPct: (v) => pct(v),
  zeroTouchShare: (v) => pct(v),
  openWorkedPct: (v) => pct(v),
  facilities: (v) => (v === null ? '—' : Number.isInteger(v) ? int(v) : dec(v, 1)),
}

/** Why a line earned nothing, when it is not simply "did not beat the average". */
const missingReason = (value: number | null, peerAvg: number | null) =>
  value === null || !Number.isFinite(value)
    ? 'no figure'
    : peerAvg === null
      ? 'no peers to compare against'
      : 'none in the period'

export function ScoreTip({
  score,
  who,
  children,
}: {
  score: Scorecard
  /** The peer group in words, e.g. "Billing · Biller". */
  who?: string
  /** The badge. */
  children: ReactNode
}) {
  const { open, id, btn, wrapper, cardProps } = useHoverCard(CARD_WIDTH)

  return (
    <span className="scoretip" {...wrapper}>
      <button
        ref={btn}
        type="button"
        className="scoretip-btn"
        aria-label={score.scored ? `Score ${formatPoints(score.total)}: how it was formed` : 'Not scored: why'}
        aria-expanded={open}
        aria-describedby={open ? id : undefined}
      >
        {children}
      </button>
      {open && (
        <span {...cardProps}>
          {!score.scored ? (
            <>
              <span className="info-card-title">Not scored</span>
              <span className="info-card-text">{unscoredReason(score)}</span>
            </>
          ) : (
            <>
              <span className="info-card-title">
                Score {formatPoints(score.total)} of a possible {SCORE_MAX}
              </span>
              {score.lowVolume && (
                <span className="info-card-text" data-testid="low-volume-reason">
                  {lowVolumeReason(score.lowVolume)}
                </span>
              )}
              <span className="info-card-text">
                Each line earns its points when the figure beats the average of their
                peers{who ? ` (${who})` : ''}; otherwise it earns 0.
              </span>
              <span className="scoretip-table" role="table">
                {score.lines.map((l) => (
                  <span key={l.key} className="scoretip-row" role="row">
                    <span role="cell" className="scoretip-k">
                      {l.label}
                      <span className="scoretip-sub">
                        {l.missing
                          ? missingReason(l.value, l.peerAvg)
                          : `${FORMAT[l.key](l.value)} vs avg ${FORMAT[l.key](l.peerAvg)}${l.lowerIsBetter ? ' · lower is better' : ''}`}
                      </span>
                    </span>
                    <span
                      role="cell"
                      className={`scoretip-pts${l.earned > 0 ? ' good' : ''}`}
                    >
                      {l.earned > 0 ? formatPoints(l.earned) : '0'}
                      <span className="scoretip-of">/{formatPoints(l.pts).replace('+', '')}</span>
                    </span>
                  </span>
                ))}
                <span className="scoretip-row" role="row">
                  <span role="cell" className="scoretip-k">
                    Penalty: {PENALTY.label.replace('Claims billed vs resolved %', 'billed vs resolved')}
                    <span className="scoretip-sub">
                      {score.penalty.applied
                        ? `${pct(score.penalty.value)}, so it applies`
                        : score.penalty.value === null
                          ? 'no figure, so it does not apply'
                          : `${pct(score.penalty.value)}, so it does not apply`}
                    </span>
                  </span>
                  <span role="cell" className={`scoretip-pts${score.penalty.applied ? ' bad' : ''}`}>
                    {score.penalty.applied ? formatPoints(score.penalty.pts) : '0'}
                  </span>
                </span>
                {score.lowVolume && (
                  <>
                    <span className="scoretip-row" role="row">
                      <span role="cell" className="scoretip-k">Before the override</span>
                      <span role="cell" className="scoretip-pts">{formatPoints(score.lowVolume.computed)}</span>
                    </span>
                    <span className="scoretip-row" role="row">
                      <span role="cell" className="scoretip-k">
                        {MINIMUM_VOLUME_LABEL}
                        <span className="scoretip-sub">
                          under the job title’s minimum: 90% of the bottom quarter’s average
                        </span>
                      </span>
                      <span role="cell" className="scoretip-pts bad">0</span>
                    </span>
                  </>
                )}
                <span className="scoretip-row scoretip-total" role="row">
                  <span role="cell" className="scoretip-k">Total</span>
                  <span role="cell" className="scoretip-pts">{formatPoints(score.total)}</span>
                </span>
              </span>
              <span className="scoretip-sub">
                Peer group: {score.peerN} people with claims, them included; cost per
                claim uses the {score.shoreN} on the same shore. Scores run from{' '}
                {formatPoints(SCORE_MIN).replace('-', '−')} to +{SCORE_MAX}.
              </span>
            </>
          )}
        </span>
      )}
    </span>
  )
}
